/**
 * Client API pour les établissements scolaires
 * Endpoint: /api/school/establishments
 * Service backend: sphere-school-service via sphere-gateway
 */

import { HttpClient } from '@sphere/shared/data-access/http'
import type {
  Establishment,
  CreateEstablishmentDto,
  UpdateEstablishmentDto,
  EstablishmentStats,
} from '@sphere/school/domain'
import type { User } from '@sphere/auth/domain'
import type { IOffsetPaginationResponse } from '@sphere/shared/domain'

const BASE_PATH = '/school/establishments'

export const establishmentsClient = {
  /**
   * Liste tous les établissements accessibles
   * Backend filtre automatiquement selon le scope utilisateur
   * @param search - Recherche textuelle (nom, UAI, ville)
   * @param limit - Nombre maximum de résultats (défaut: 100)
   */
  async list(search?: string, limit: number = 100): Promise<Establishment[]> {
    const params: Record<string, any> = { limit, page: 1 }
    if (search) {
      params.q = search
    }
    const { data } = await HttpClient.get<
      IOffsetPaginationResponse<Establishment>
    >(BASE_PATH, {
      params,
    })
    return data.data
  },

  /**
   * Liste les établissements ayant au moins une classe
   * Admin uniquement
   */
  async getWithClasses(search?: string): Promise<
    Array<{
      id: string
      name: string
      fullName: string
      uai: string
      city: string | null
      category: 'public' | 'private' | null
      classCount: number
    }>
  > {
    const params = search ? `?q=${encodeURIComponent(search)}` : ''
    const { data } = await HttpClient.get<
      Array<{
        id: string
        name: string
        fullName: string
        uai: string
        city: string | null
        category: 'public' | 'private' | null
        classCount: number
      }>
    >(`${BASE_PATH}/with-classes${params}`)
    return data
  },

  /**
   * Récupère un établissement par ID
   */
  async getById(id: string): Promise<Establishment> {
    const { data } = await HttpClient.get<Establishment>(`${BASE_PATH}/${id}`)
    return data
  },

  /**
   * Récupère un établissement par UAI (identifiant unique)
   */
  async getByUai(uai: string): Promise<Establishment> {
    const { data } = await HttpClient.get<Establishment>(
      `${BASE_PATH}/by-uai`,
      {
        params: { uai },
      }
    )
    return data
  },

  /**
   * Crée un nouvel établissement
   * Nécessite: canCreate au niveau global (bootstrap)
   */
  async create(dto: CreateEstablishmentDto): Promise<Establishment> {
    const { data } = await HttpClient.post<Establishment>(BASE_PATH, dto)
    return data
  },

  /**
   * Met à jour un établissement
   * Nécessite: capabilities.canEdit sur l'établissement
   */
  async update(
    id: string,
    dto: UpdateEstablishmentDto
  ): Promise<Establishment> {
    const { data } = await HttpClient.patch<Establishment>(
      `${BASE_PATH}/${id}`,
      dto
    )
    return data
  },

  /**
   * Supprime un établissement
   * Nécessite: capabilities.canDelete sur l'établissement
   */
  async delete(id: string): Promise<void> {
    await HttpClient.delete(`${BASE_PATH}/${id}`)
  },

  /**
   * Récupère les statistiques d'un établissement
   * (nombre de classes, élèves, profs, matières)
   */
  async getStats(id: string): Promise<EstablishmentStats> {
    const { data } = await HttpClient.get<EstablishmentStats>(
      `${BASE_PATH}/${id}/stats`
    )
    return data
  },

  /**
   * Récupère les membres actifs d'un établissement
   */
  async getActiveMembers(id: string): Promise<User[]> {
    const { data } = await HttpClient.get<User[]>(
      `${BASE_PATH}/${id}/members/active`
    )
    return data
  },
}
